import React, {useEffect, useState, useTransition} from 'react';
import {useSelector} from "react-redux";

import {useAppKitProvider} from "@reown/appkit/react";
import {useTranslation} from "react-i18next";
import {ethers} from "ethers";

import {
    SIZE,
    ERROR,
    WARNING,
    SUCCESS,
    INFORMATION,
    INTERACTION,
    STEPS_STATUS,
    BUTTON_VARIANT,
    WALLET_ERROR_CODE,
    formatNumberToEuropeanStyle
} from "utils";
import {useSendTransactionHashMutation} from "store/Bazaar/bazaar.api";
import {useCheckListingMutation} from "store/Global/global.api";
import {globalService} from "store/Global/globalService";
import {useConnectedWallet, useGMTEvents} from "hooks";
import {ERC20, KOMPETEMarketplaceV2ABI} from "web3/ABI";
import {useActionContext} from "../useActionContext";
import {CHAIN_TO_PARAMS_MAP} from "web3/chains";
import {useModalsContext} from "layouts";

import {ButtonV2, Information, Modal} from "components/atoms";
import {InfoItem, Process} from "./components"

const BazaarCurrentItemConfirmPurchaseModal = () => {
    const [isPending, startTransition] = useTransition()
    const {isConnected, connectedChainId} = useConnectedWallet()
    const {walletProvider} = useAppKitProvider("eip155");
    const {setCurrentModal, currentModal} = useModalsContext()
    const {setActionDetails} = useActionContext()
    const {NFTSaleBazaar} = useGMTEvents()
    const {t} = useTranslation()

    const {userData} = useSelector(state => state.authV2.signIn)
    const {name, tokenId, priceUSD, listing} = currentModal.data
    const {id, order, price, chain, signature} = listing

    const [estimatedFee, setEstimatedFee] = useState(null)

    const [informationStatus, setInformationStatus] = useState(userData.kompeteBalance >= price ? {
        type: INFORMATION.WARNING,
        text: WARNING.LISTING_PURCHASE
    } : {
        type: INFORMATION.ERROR,
        text: ERROR.INSUFFICIENT_KOMPETE_BALANCE
    })

    const [sendTransactionHash] = useSendTransactionHashMutation()
    const [checkListing] = useCheckListingMutation()

    const isConnectedChainCorrect = isConnected && (connectedChainId === Number(chain))

    useEffect(() => {
        startBuyTransaction(true).then(result => setEstimatedFee(result))

        if (!isConnectedChainCorrect) {
            setInformationStatus({
                    type: INFORMATION.ERROR,
                    text: ERROR.INVALID_CHAIN
                }
            )
        }

        checkListing({order_id: id})
            .unwrap().then(res => {
            if (res.data.canceled) {
                setActionDetails({
                    type: INTERACTION.CANCEL_LISTING,
                    isSuccess: true,
                    data: {
                        order_id: id,
                    },
                })
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const startBuyTransaction = async (isEstimate) => {

        const calculateEstimationToUSD = async (estimation, provider, ethPrice) => {
            const gasPrice = await provider.getGasPrice();
            const totalCostInWei = estimation.mul(gasPrice);
            const totalCostInEth = ethers.utils.formatEther(totalCostInWei);
            return parseFloat(totalCostInEth) * ethPrice;
        }

        const sendTransaction = (tx) => {
            const {id} = userData

            sendTransactionHash({transaction_hash: tx.hash, order_id: listing.id})
            NFTSaleBazaar(id, tokenId, price, "kompete", tx.hash)
        }

        const transactionResultMessage = (status) => {
            if (status) {
                setInformationStatus({
                    type: INFORMATION.SUCCESS,
                    text: SUCCESS.LISTING_PURCHASE
                })

                setActionDetails({
                    type: INTERACTION.BUY,
                    isSuccess: true,
                    data: {
                        order_id: id,
                    },
                })

                setTimeout(() => {
                    setCurrentModal({status: STEPS_STATUS.CLOSE})
                }, 3000)
            } else {
                setInformationStatus({
                    type: INFORMATION.ERROR,
                    text: ERROR.WRONG
                })
            }
        }

        try {
            const chainParamsMap = CHAIN_TO_PARAMS_MAP[chain]
            const {rpcURL, addresses} = chainParamsMap

            const provider = new ethers.providers.JsonRpcProvider(rpcURL)
            const buyerProvider = new ethers.providers.Web3Provider(walletProvider)
            const marketplaceContract = new ethers.Contract(addresses.MARKETPLACE_ADDRESS_V2, KOMPETEMarketplaceV2ABI, provider)
            const paymentContract = new ethers.Contract(addresses.PAYMENT_ADDRESS, ERC20, provider)

            const signer = buyerProvider.getSigner()

            const allowance = await paymentContract.allowance(signer.getAddress(), addresses.MARKETPLACE_ADDRESS_V2)

            const contractCall = async (isOnlyEstimation) => {
                const estimation = (await marketplaceContract
                    .connect(signer)
                    .estimateGas
                    .executeLazyListing({
                        ...order,
                        signature,
                        used: false,
                    }))
                    .mul(110)
                    .div(100)

                if (isOnlyEstimation) {
                    return estimation
                }

                return await marketplaceContract
                    .connect(signer)
                    .executeLazyListing({
                        ...order,
                        signature,
                        used: false,
                    }, {
                        gasLimit: estimation,
                    })
            }

            if (allowance.gt(price)) {
                if (isEstimate) {
                    const estimation = await contractCall(true)

                    const ethPrice = await globalService.getEthPrice()
                    const estimationToUSD = await calculateEstimationToUSD(estimation, provider, ethPrice)

                    return `~ $${estimationToUSD.toFixed(5)}`
                } else {
                    const tx = await contractCall()

                    sendTransaction(tx)

                    const result = await tx.wait()

                    transactionResultMessage(result.status)
                }
            } else {
                if (isEstimate) {
                    const estimation = (await paymentContract.connect(signer)
                        .estimateGas
                        .approve(addresses.MARKETPLACE_ADDRESS_V2, ethers.constants.MaxUint256))
                        .mul(110)
                        .div(100)

                    const ethPrice = await globalService.getEthPrice()
                    const estimationToUSD = await calculateEstimationToUSD(estimation, provider, ethPrice)

                    return `~ $${estimationToUSD.toFixed(5)}`
                } else {
                    await paymentContract.connect(signer)
                        .approve(addresses.MARKETPLACE_ADDRESS_V2, ethers.constants.MaxUint256)

                    const allowance = await paymentContract.allowance(signer.getAddress(), addresses.MARKETPLACE_ADDRESS_V2)

                    if (allowance.lt(price)) {
                        setInformationStatus({
                            type: INFORMATION.ERROR,
                            text: ERROR.INSUFFICIENT_KOMPETE_ALLOWANCE
                        })

                        return
                    }

                    const tx = await contractCall()

                    sendTransaction(tx)

                    const result = await tx.wait()

                    transactionResultMessage(result.status)
                }
            }
        } catch (error) {
            if (isEstimate) {
                return 0
            } else {
                switch (error.code) {
                    case "ACTION_REJECTED":
                    case WALLET_ERROR_CODE.USER_REJECT_REQUEST:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.USER_DENIED_SIGNATURE})
                        break
                    case "CALL_EXCEPTION":
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.TRANSACTION_FAILED})
                        break
                    default:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.NO_COSMETIC})
                }
            }
        }
    }

    const handleConfirm = () => {
        startTransition(async () => {
            await startBuyTransaction()
        })
    }

    const initialDisabledButton = isPending || informationStatus.type === INFORMATION.SUCCESS
    const isDisabledButton = initialDisabledButton || estimatedFee === null || userData.kompeteBalance < price || !isConnectedChainCorrect

    return (
        <Modal
            isOpen={true}
            size={SIZE.SMALL}
            title={t("confirmPurchaseModalTitle")}
            onClose={() => !initialDisabledButton ? setCurrentModal({status: STEPS_STATUS.CLOSE}) : null}
        >
            <div className="bazaar_current-item_purchase-confirm-modal">
                <span className="bazaar_current-item_purchase-confirm-modal_under-title">{t("confirmPurchaseModalSubTitle")}</span>
                <div className="bazaar_current-item_purchase-confirm-modal_item-info">
                    <InfoItem name="item" value={name}/>
                    <InfoItem name="tokenId" value={tokenId}/>
                </div>
                <div className="bazaar_current-item_purchase-confirm-modal_price-info">
                    <InfoItem name="price" value={`${formatNumberToEuropeanStyle(price)} KOMPETE`}/>
                    <InfoItem name="usdValue" value={priceUSD.replace('/', '~ ')}/>
                    <InfoItem name="fee" value={estimatedFee} isLoading={estimatedFee === null}/>
                </div>
                {isPending ? <Process/> : <Information
                    type={informationStatus.type}
                    text={informationStatus.text}
                />}
                <div className="bazaar_current-item_purchase-confirm-modal_btns">
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        disabled={initialDisabledButton}
                        onClick={() => setCurrentModal({status: STEPS_STATUS.CLOSE})}
                    >
                        cancel
                    </ButtonV2>
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        onClick={handleConfirm}
                        disabled={isDisabledButton}
                        variant={BUTTON_VARIANT.RED}
                    >
                        confirm
                    </ButtonV2>
                </div>
            </div>
        </Modal>
    )
}

export default BazaarCurrentItemConfirmPurchaseModal